import React, { useState, useMemo } from 'react'
import { Spin } from 'antd'
import type { CalendarProps } from './types'
import { CalendarHeader } from './CalendarHeader'
import { DayView } from './views/DayView'
import { WeekView } from './views/WeekView'
import { MonthView } from './views/MonthView'
import { ResourceDayView } from './views/ResourceDayView'
import { ResourceWeekView } from './views/ResourceWeekView'
import { useCalendarNavigation } from './hooks/useCalendarNavigation'
import { useEventSelection } from './hooks/useEventSelection'
import { getUILabel } from './utils/locale'

import './styles.css'

export const Calendar: React.FC<CalendarProps> = ({
  events,
  doctors,
  resources,
  resourceGrouping = 'by-resource',
  defaultView = 'week',
  defaultDate,
  selectedDoctorId: controlledDoctorId,
  editable = true,
  multiSelect = true,
  slotDuration: controlledSlotDuration,
  showSlotDurationPicker = false,
  startHour = 8,
  endHour = 20,
  locale = 'sr',
  loading = false,
  onEventClick,
  onSlotClick,
  onEventDrop,
  // onEventResize - reserved for future use
  onViewChange,
  onDateChange,
  onDoctorFilterChange,
  onSelectionChange,
  onSlotDurationChange,
}) => {
  const [internalDoctorId, setInternalDoctorId] = useState<string | null>(null)
  const [internalSlotDuration, setInternalSlotDuration] = useState(30)

  const selectedDoctorId = controlledDoctorId ?? internalDoctorId
  const slotDuration = controlledSlotDuration ?? internalSlotDuration

  const navigation = useCalendarNavigation({
    defaultDate,
    defaultView,
    onDateChange,
    onViewChange,
  })

  // Event selection
  const selection = useEventSelection({
    events,
    multiSelect,
    onSelectionChange,
  })

  // Filter events by doctor
  const filteredEvents = useMemo(() => {
    if (!selectedDoctorId) return events
    return events.filter((event) => event.doctor.id === selectedDoctorId)
  }, [events, selectedDoctorId])

  const handleDoctorChange = (doctorId: string | null) => {
    setInternalDoctorId(doctorId)
    onDoctorFilterChange?.(doctorId)
  }

  const handleSlotDurationChange = (duration: number) => {
    setInternalSlotDuration(duration)
    onSlotDurationChange?.(duration)
  }

  const handleDayClick = (date: Date) => {
    navigation.goToDate(date)
    navigation.changeView('day')
  }

  const handleEventClick = (
    event: CalendarEvent,
    modifiers?: { ctrl?: boolean; shift?: boolean }
  ) => {
    // If multi-select is enabled, toggle selection
    if (multiSelect && (modifiers?.ctrl || modifiers?.shift)) {
      selection.toggleSelection(event.id, modifiers)
    } else {
      // Otherwise, just notify parent and optionally select
      selection.selectEvent(event.id)
      onEventClick?.(event)
    }
  }

  const renderView = () => {
    switch (navigation.view) {
      case 'day':
        return (
          <DayView
            date={navigation.currentDate}
            events={filteredEvents}
            slotDuration={slotDuration}
            startHour={startHour}
            endHour={endHour}
            editable={editable}
            locale={locale}
            selectedEventIds={selection.selectedIds}
            onEventClick={handleEventClick}
            onSlotClick={onSlotClick}
            onEventDrop={onEventDrop}
          />
        )

      case 'week':
        return (
          <WeekView
            weekStart={navigation.weekStart}
            events={filteredEvents}
            slotDuration={slotDuration}
            startHour={startHour}
            endHour={endHour}
            editable={editable}
            locale={locale}
            selectedEventIds={selection.selectedIds}
            onEventClick={handleEventClick}
            onSlotClick={onSlotClick}
            onEventDrop={onEventDrop}
          />
        )

      case 'month':
        return (
          <MonthView
            month={navigation.currentDate}
            events={filteredEvents}
            locale={locale}
            onEventClick={onEventClick}
            onDayClick={handleDayClick}
          />
        )

      case 'resource-day':
        if (!resources || resources.length === 0) {
          return (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              {locale === 'sr'
                ? 'Nema dostupnih resursa za prikaz'
                : 'No resources available to display'}
            </div>
          )
        }
        return (
          <ResourceDayView
            date={navigation.currentDate}
            events={filteredEvents}
            resources={resources}
            slotDuration={slotDuration}
            startHour={startHour}
            endHour={endHour}
            editable={editable}
            locale={locale}
            selectedEventIds={selection.selectedIds}
            onEventClick={handleEventClick}
            onSlotClick={onSlotClick}
            onEventDrop={onEventDrop}
          />
        )

      case 'resource-week':
        if (!resources || resources.length === 0) {
          return (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              {locale === 'sr'
                ? 'Nema dostupnih resursa za prikaz'
                : 'No resources available to display'}
            </div>
          )
        }
        return (
          <ResourceWeekView
            weekStart={navigation.weekStart}
            events={filteredEvents}
            resources={resources}
            grouping={resourceGrouping}
            slotDuration={slotDuration}
            startHour={startHour}
            endHour={endHour}
            editable={editable}
            locale={locale}
            selectedEventIds={selection.selectedIds}
            onEventClick={handleEventClick}
            onSlotClick={onSlotClick}
            onEventDrop={onEventDrop}
          />
        )
    }
  }

  return (
    <div className="perfectamed-calendar">
      <CalendarHeader
        currentDate={navigation.currentDate}
        view={navigation.view}
        locale={locale}
        doctors={doctors}
        selectedDoctorId={selectedDoctorId}
        slotDuration={slotDuration}
        showSlotDurationPicker={showSlotDurationPicker}
        onPrevious={navigation.goToPrevious}
        onNext={navigation.goToNext}
        onToday={navigation.goToToday}
        onViewChange={navigation.changeView}
        onDoctorChange={handleDoctorChange}
        onSlotDurationChange={handleSlotDurationChange}
      />

      <div className="perfectamed-calendar__content">
        {loading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px',
            }}
          >
            <Spin size="large" tip={getUILabel('loading', locale)} />
          </div>
        ) : (
          renderView()
        )}
      </div>
    </div>
  )
}
